import type { App } from 'vue'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'
import { Logging } from '@newrelic/browser-agent/features/logging'
import { JSErrors } from '@newrelic/browser-agent/features/jserrors'
import { Ajax } from '@newrelic/browser-agent/features/ajax'
import { SessionTrace } from '@newrelic/browser-agent/features/session_trace'
import { SessionReplay } from '@newrelic/browser-agent/features/session_replay'
import { SoftNav } from '@newrelic/browser-agent/features/soft_navigations'
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event'
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing'
import { resolveAPIEndpoints } from '@400f/frontend-constants'

// NewRelic Browser Agent instance
const browserAgent: { instance: BrowserAgent | null } = { instance: null }

export const captureException = (
  error: Error | string | unknown,
  context?: Record<string, string | number | boolean | null | object>
): void => {
  if (browserAgent.instance) {
    browserAgent.instance.noticeError(
      error instanceof Error || typeof error === 'string'
        ? error
        : String(error),
      context
    )
  }
}

export const captureMessage = (
  message: string,
  captureContext?: Record<string, string | number | boolean | null | object>
): void => {
  if (browserAgent.instance) {
    // undefinedの値を除外してからNewRelicに送信
    const cleanedContext = captureContext
      ? (() => {
          const filtered = Object.fromEntries(
            Object.entries(captureContext).filter(
              ([, value]) => value !== undefined
            )
          )
          return Object.keys(filtered).length > 0 ? filtered : undefined
        })()
      : undefined

    browserAgent.instance.log(
      message,
      cleanedContext
        ? { level: 'WARN' as const, customAttributes: cleanedContext }
        : { level: 'WARN' as const }
    )
  }
}

export const setupNewRelic = (_app: App): void => {
  if (import.meta.env.MODE === 'test') {
    return
  }

  // NewRelic Browser Agent初期化
  try {
    browserAgent.instance = new BrowserAgent({
      loader_config: {
        accountID: import.meta.env.VITE_NEWRELIC_ACCOUNT_ID || '',
        trustKey: import.meta.env.VITE_NEWRELIC_TRUST_KEY || '',
        agentID: import.meta.env.VITE_NEWRELIC_AGENT_ID || '',
        licenseKey: import.meta.env.VITE_NEWRELIC_LICENSE_KEY || '',
        applicationID: import.meta.env.VITE_NEWRELIC_APPLICATION_ID || '',
      },
      // https://github.com/newrelic/newrelic-browser-agent/blob/HEAD/src/common/config/info.js
      info: {
        licenseKey: import.meta.env.VITE_NEWRELIC_LICENSE_KEY || '',
        applicationID: import.meta.env.VITE_NEWRELIC_APPLICATION_ID || '',
        jsAttributes: {
          environment: import.meta.env.MODE,
        },
        sa: 1,
      },
      features: [
        Logging, // カスタムログ記録 (captureMessage用)
        JSErrors, // JavaScript エラー監視 (captureException用)
        Ajax, // Ajax/Fetch API監視
        SessionTrace, // セッション詳細追跡
        SessionReplay, // エラー時画面リプレイ
        SoftNav, // Single Page Application監視
        PageViewEvent, // ページビューイベント追跡
        PageViewTiming, // ページパフォーマンス詳細追跡
      ],
      // https://github.com/newrelic/newrelic-browser-agent/blob/HEAD/src/common/config/init.js
      init: {
        // 分散トレーシング設定
        distributed_tracing: {
          enabled: true,
          // HTTPヘッダーに「newrelic」を追加するための設定
          cors_use_newrelic_header: true,
          // HTTPヘッダーに「traceparent」「tracestate」を追加するための設定
          cors_use_tracecontext_headers: true,
          // NewRelic用のヘッダーを付与するリクエスト先URLの一覧
          // frontend-constantsからAPIエンドポイントを取得して動的に生成
          allowed_origins: (() => {
            const mode = import.meta.env.MODE
            const envMode =
              mode === 'development'
                ? 'alpha'
                : (mode as 'alpha' | 'demo' | 'production')

            try {
              const apiEndpoints = resolveAPIEndpoints(envMode)

              // すべてのAPIエンドポイントからoriginを抽出
              const origins = Object.values(apiEndpoints.apis).map(url => {
                const urlObj = new URL(url)
                return `${urlObj.protocol}//${urlObj.host}`
              })

              // 重複を除去
              return [...new Set(origins)]
            } catch (error) {
              console.error(
                'Failed to resolve API endpoints from frontend-constants:',
                error
              )
              // エラー時は空配列を返す（分散トレーシングは動作しないが、アプリケーションは継続動作）
              return []
            }
          })(),
        },

        // プライバシー設定
        privacy: {
          cookies_enabled: true,
        },

        // Ajax監視の除外設定
        ajax: {
          enabled: true,
          autoStart: false,
          deny_list: [
            'googletagmanager.com',
            'graph.facebook.com',
            'connect.facebook.net',
            'chrome-extension://',
            'chrome://',
            'file://',
            'extensions/',
          ],
        },

        // JavaScriptエラー監視
        jserrors: {
          enabled: true,
          autoStart: false,
        },

        // パフォーマンスメトリクス
        metrics: {
          enabled: true,
          autoStart: false,
        },

        // SPA監視
        soft_navigations: {
          enabled: true,
          autoStart: false,
        },

        // セッションリプレイ
        session_replay: {
          enabled: true,
          autoStart: false,
          sampling_rate: 0,
          error_sampling_rate: 100,
        },

        // セッショントレース
        session_trace: {
          enabled: true,
          autoStart: false,
        },

        // logging
        logging: {
          enabled: true,
          autoStart: false,
        },
      },
    })

    // setErrorHandlerはエラーがNewRelicに送信される前に呼び出されるフック
    // https://docs.newrelic.com/jp/docs/browser/new-relic-browser/browser-apis/seterrorhandler/
    browserAgent.instance.setErrorHandler((error: string | Error) => {
      console.info({ error })

      // スタックトレース内からErrorをskipするパターンを検索
      if (error instanceof Error && error.stack) {
        const stackIgnorePatterns = [
          /file\/\//,

          // ブラウザ拡張機能のエラーを除外
          /chrome-extension:\/\//,
          /extensions:\//,
        ]

        const shouldIgnore = stackIgnorePatterns.some(pattern =>
          pattern.test(error.stack!)
        )

        if (shouldIgnore) {
          console.log('ignore stack error', error.stack)
          return true
        }
      }

      // 無視するエラーメッセージパターン
      const ignorePatterns = [
        // 認証関連（通常のログアウト処理など）
        /Unauthorized/,
        /Received status code 401/,
        /Request failed with status code 401/,
        /Response not successful: Received status code 401/,

        // タイムアウト（一時的なネットワーク問題）
        /timeout/,

        // ネットワークエラー（オフライン状態など）
        /Network Error/,
        /Failed to fetch/,
        /Load failed/,
        /Request aborted/,
        /socket is not opened/,

        // WebSocket 接続失敗（クライアント網不安定によるもの）は PageAction で別途追跡
        /Failed to connect WebSocket/,

        // 特定の拡張機能によるエラー
        /learningToolsRuntimeBridge/,

        // CefSharp (Chromium 組込みの .NET ラッパー) の JS バインディングが投げる無害なノイズ。
        // Outlook SafeLinks 等のリンク自動スキャン由来で、実ユーザー・アプリのコードとは無関係。
        // https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
        /Object Not Found Matching Id/,
      ]

      // エラーメッセージが無視パターンにマッチするかチェック
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      const shouldIgnore = ignorePatterns.some(pattern =>
        pattern.test(errorMessage)
      )

      if (shouldIgnore) {
        console.log('ignore message', errorMessage)
        // ignoreErrorsでマッチした場合は送信しない
        return true
      }

      // エラーを送信
      return false
    })

    // autoStart: falseの機能を開始
    browserAgent.instance.start()

    // addReleaseでSourceMapとの照合を確実にする
    // GitHub ActionsのSourceMapアップロード時の--releaseNameと--releaseIdと同じ値を設定
    const releaseName = 'dolphin' // プロジェクト名
    const gitCommitHash = import.meta.env.VITE_GIT_COMMIT_HASH || 'unknown'
    const mode = import.meta.env.MODE || 'development'
    const releaseId = `${gitCommitHash}-${mode}` // コミットハッシュ-環境
    browserAgent.instance.addRelease(releaseName, releaseId)
  } catch (error) {
    console.warn('NewRelic Browser Agent initialization failed:', error)
  }
}

export const trackSession = (longSessionId: string): void => {
  if (browserAgent.instance) {
    browserAgent.instance.setCustomAttribute('long_session_id', longSessionId)
  }
}

export const trackPlanner = (user: { id: string; username: string }): void => {
  if (browserAgent.instance) {
    browserAgent.instance.setUserId(user.id)
    // usernameをカスタム属性として設定
    browserAgent.instance.setCustomAttribute('username', user.username)
  }
}

export const unsetTrackingPlanner = (): void => {
  if (browserAgent.instance) {
    browserAgent.instance.setUserId(null)
    // trackPlannerで設定したusernameカスタム属性を削除
    browserAgent.instance.setCustomAttribute('username', '', false)
  }
}

export const addPageAction = (
  name: string,
  attributes?: Record<string, string | number | boolean | null>
): void => {
  if (browserAgent.instance) {
    browserAgent.instance.addPageAction(name, attributes)
  }
}
